import React from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Table,
  Tag,
  Button,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  UserAddOutlined,
  PlusOutlined,
  FileTextOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAdminStats } from '../hooks';
import { useRecentActivity } from '../hooks/useRecentActivity';

const { Title, Text } = Typography;

const ORANGE  = '#FE5A1D';
const GREEN   = '#52c41a';
const BLUE    = '#1677ff';
const PURPLE  = '#722ed1';
const WARNING = '#fa8c16';

// ── Chart mock data ──────────────────────────────────────────
const revenueData = [
  { month: 'Jan', revenue: 4200, users: 240 },
  { month: 'Feb', revenue: 3800, users: 310 },
  { month: 'Mar', revenue: 5100, users: 290 },
  { month: 'Apr', revenue: 4700, users: 380 },
  { month: 'May', revenue: 6200, users: 420 },
  { month: 'Jun', revenue: 5800, users: 460 },
  { month: 'Jul', revenue: 7400, users: 510 },
  { month: 'Aug', revenue: 6900, users: 490 },
];

const activityData = [
  { day: 'Mon', active: 420, pending: 90 },
  { day: 'Tue', active: 380, pending: 130 },
  { day: 'Wed', active: 510, pending: 75 },
  { day: 'Thu', active: 460, pending: 110 },
  { day: 'Fri', active: 540, pending: 60 },
  { day: 'Sat', active: 310, pending: 40 },
  { day: 'Sun', active: 270, pending: 30 },
];

const listingPerformanceData = [
  { name: 'Cars',        value: 52 },
  { name: 'Motorcycles', value: 22 },
  { name: 'Trucks',      value: 16 },
  { name: 'Parts',       value: 10 },
];

const PIE_COLORS = [ORANGE, '#FF8E5E', '#262626', '#BFBFBF'];

// ── Custom recharts tooltip ──────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: 10,
        padding: '10px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 700, color: '#1a1a1a', marginBottom: 6 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ color: '#8c8c8c', textTransform: 'capitalize' }}>{p.dataKey}:</span>
          <span style={{ fontWeight: 600, color: '#1a1a1a' }}>
            {p.dataKey === 'revenue' ? `${p.value.toLocaleString()} ALL` : p.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Activity type badge ──────────────────────────────────────
const ActivityTag: React.FC<{ type: string }> = ({ type }) => {
  const { t } = useTranslation();
  switch (type) {
    case 'APPROVE_LISTING':
    case 'listing_approved':
      return <Tag color="success">{t('admin.stats.tag_approved')}</Tag>;
    case 'REJECT_LISTING':
      return <Tag color="error">{t('admin.stats.tag_rejected')}</Tag>;
    case 'NEW_SUBMISSION':
    case 'new_submission':
      return <Tag color="processing">{t('admin.stats.tag_new_submission')}</Tag>;
    default:
      return <Tag className="ant-tag-orange">{type}</Tag>;
  }
};

// ── Stat Card ────────────────────────────────────────────────
interface StatCardProps {
  title: string;
  value: React.ReactNode;
  iconClass: string;
  icon: React.ReactNode;
  trend?: { label: string; up?: boolean; neutral?: boolean };
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  iconClass,
  icon,
  trend,
  loading,
}) => (
  <Card bordered={false} className="admin-stat-card" loading={loading}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
      <div className={`admin-stat-icon-wrapper ${iconClass}`}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="admin-stat-title">{title}</div>
        <div className="admin-stat-value">{value}</div>
        {trend && (
          <div
            className={
              trend.neutral
                ? 'admin-stat-trend-neutral'
                : trend.up
                ? 'admin-stat-trend-up'
                : 'admin-stat-trend-down'
            }
          >
            {!trend.neutral && (trend.up ? <RiseOutlined /> : <FallOutlined />)}
            {trend.label}
          </div>
        )}
      </div>
    </div>
  </Card>
);

// ── Main Component ────────────────────────────────────────────
export const AdminStats: React.FC = () => {
  const { t } = useTranslation();
  const { loading, stats } = useAdminStats();
  const {
    loading: activityLoading,
    data: recentActivity,
    total: activityTotal,
    page: activityPage,
    fetchRecentActivity,
  } = useRecentActivity();

  const activityColumns = [
    {
      title: t('admin.stats.col_listing_user'),
      key: 'activity',
      render: (record: any) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--admin-text-primary)', fontSize: 13 }}>
            {record.listing || '—'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)', marginTop: 1 }}>
            {record.user}
          </div>
        </div>
      ),
    },
    {
      title: t('admin.stats.col_type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <ActivityTag type={type} />,
    },
    {
      title: t('admin.stats.col_time'),
      dataIndex: 'time',
      key: 'time',
      width: 110,
      render: (time: string) => (
        <Text style={{ fontSize: 12, color: 'var(--admin-text-secondary)' }}>{time}</Text>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={20} style={{ width: '100%', paddingBottom: 24 }}>

      {/* ── Page Header ── */}
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">{t('admin.stats.page_title')}</h2>
          <p className="admin-section-subtitle">
            {t('admin.stats.page_subtitle')}
          </p>
        </div>
        <div className="admin-quick-actions">
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            className="admin-quick-action-btn"
          >
            {t('admin.stats.btn_add_user')}
          </Button>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            className="admin-quick-action-btn"
          >
            {t('admin.stats.btn_reports')}
          </Button>
          <Button
            icon={<PlusOutlined />}
            className="admin-quick-action-btn"
          >
            {t('admin.stats.btn_new_package')}
          </Button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_active_listings')}
            value={stats.activeListings?.toLocaleString() ?? '—'}
            iconClass="admin-stat-icon-orange"
            icon={<CarOutlined />}
            trend={{ label: t('admin.stats.trend_from_last_week'), up: true }}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_total_revenue')}
            value={
              stats.totalRevenue != null
                ? `${Number(stats.totalRevenue).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ALL`
                : '—'
            }
            iconClass="admin-stat-icon-green"
            icon={<DollarOutlined />}
            trend={{ label: t('admin.stats.trend_from_last_month'), up: true }}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_pending_approvals')}
            value={stats.todaySubmissions?.toLocaleString() ?? '—'}
            iconClass="admin-stat-icon-warning"
            icon={<ExclamationCircleOutlined />}
            trend={{ label: t('admin.stats.trend_requires_attention'), neutral: true }}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_total_users')}
            value="8,492"
            iconClass="admin-stat-icon-blue"
            icon={<UserAddOutlined />}
            trend={{ label: t('admin.stats.trend_new_users_today'), up: true }}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ── Secondary KPI row ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_active_dealers')}
            value="342"
            iconClass="admin-stat-icon-purple"
            icon={<ShopOutlined />}
            trend={{ label: t('admin.stats.trend_approved_week'), up: true }}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_approved_today')}
            value="18"
            iconClass="admin-stat-icon-green"
            icon={<CheckCircleOutlined />}
            trend={{ label: t('admin.stats.trend_vs_yesterday'), up: true }}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_credit_transactions')}
            value="1,204"
            iconClass="admin-stat-icon-orange"
            icon={<DollarOutlined />}
            trend={{ label: t('admin.stats.trend_vs_last_week'), up: true }}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            title={t('admin.stats.stat_revenue_month')}
            value="ALL 24,810"
            iconClass="admin-stat-icon-purple"
            icon={<RiseOutlined />}
            trend={{ label: t('admin.stats.trend_vs_last_month'), up: true }}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ── Charts row 1: Revenue + Listing breakdown ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{t('admin.stats.chart_revenue_trends')}</span>
                <Text style={{ fontSize: 12, color: 'var(--admin-text-secondary)', fontWeight: 400 }}>
                  {t('admin.stats.chart_last_8_months')}
                </Text>
              </div>
            }
            bordered={false}
            className="admin-chart-card"
          >
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={revenueData} margin={{ top: 6, right: 12, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={ORANGE} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={ORANGE} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={BLUE} stopOpacity={0.12} />
                    <stop offset="95%" stopColor={BLUE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8c8c8c', fontSize: 12 }}
                  dy={8}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8c8c8c', fontSize: 12 }}
                  dx={-4}
                  tickFormatter={(v: number) => `${v.toLocaleString()} ALL`}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={ORANGE}
                  strokeWidth={2.5}
                  fill="url(#gradRevenue)"
                  dot={false}
                  activeDot={{ r: 5, fill: ORANGE, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>

            <div className="admin-chart-legend">
              <div className="admin-chart-legend-item">
                <div className="admin-chart-legend-dot" style={{ background: ORANGE }} />
                {t('admin.stats.chart_monthly_revenue')}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={t('admin.stats.chart_listing_breakdown')}
            bordered={false}
            className="admin-chart-card"
            style={{ height: '100%' }}
          >
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={listingPerformanceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={88}
                  paddingAngle={4}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {listingPerformanceData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value: any, name: any) => [`${value}%`, name]}
                  contentStyle={{
                    borderRadius: 10,
                    border: '1px solid #e8e8e8',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    fontSize: 13,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>

            <div className="admin-chart-legend">
              {listingPerformanceData.map((entry, i) => (
                <div key={entry.name} className="admin-chart-legend-item">
                  <div
                    className="admin-chart-legend-dot"
                    style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                  />
                  {entry.name}
                  <span style={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}>
                    {entry.value}%
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Charts row 2: Weekly Activity + Recent Feed ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{t('admin.stats.chart_weekly_activity')}</span>
                <Text style={{ fontSize: 12, color: 'var(--admin-text-secondary)', fontWeight: 400 }}>
                  {t('admin.stats.chart_active_vs_pending')}
                </Text>
              </div>
            }
            bordered={false}
            className="admin-chart-card"
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={activityData}
                margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
                barSize={14}
                barGap={4}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8c8c8c', fontSize: 12 }}
                  dy={8}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8c8c8c', fontSize: 12 }}
                  dx={-4}
                />
                <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                <Bar dataKey="active"  fill={ORANGE} radius={[4, 4, 0, 0]} />
                <Bar dataKey="pending" fill="#E5E5E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            <div className="admin-chart-legend">
              <div className="admin-chart-legend-item">
                <div className="admin-chart-legend-dot" style={{ background: ORANGE }} />
                {t('admin.stats.chart_active_users')}
              </div>
              <div className="admin-chart-legend-item">
                <div className="admin-chart-legend-dot" style={{ background: '#E5E5E5' }} />
                {t('admin.stats.chart_pending')}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t('admin.stats.section_recent_activity')}
            bordered={false}
            className="admin-table-card"
            extra={
              <Button type="link" style={{ color: 'var(--admin-primary)', fontWeight: 600, padding: 0 }}>
                {t('admin.stats.btn_view_all')}
              </Button>
            }
          >
            <Table
              className="admin-table"
              dataSource={recentActivity}
              columns={activityColumns}
              loading={activityLoading || activityTotal === undefined}
              pagination={{
                total: activityTotal || 0,
                current: activityPage || 1,
                pageSize: 4,
                showSizeChanger: false,
                responsive: true,
                onChange: (pageNum, pageSize) => fetchRecentActivity(pageNum, pageSize),
              }}
              size="small"
              rowKey="id"
              scroll={{ x: true }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Sales Growth Chart ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{t('admin.stats.chart_new_users_per_month')}</span>
                <Text style={{ fontSize: 12, color: 'var(--admin-text-secondary)', fontWeight: 400 }}>
                  {t('admin.stats.chart_registration_growth')}
                </Text>
              </div>
            }
            bordered={false}
            className="admin-chart-card"
          >
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={revenueData} margin={{ top: 6, right: 12, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradNewUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={PURPLE} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={PURPLE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8c8c8c', fontSize: 12 }}
                  dy={8}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8c8c8c', fontSize: 12 }}
                  dx={-4}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke={PURPLE}
                  strokeWidth={2.5}
                  fill="url(#gradNewUsers)"
                  dot={false}
                  activeDot={{ r: 5, fill: PURPLE, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>

            <div className="admin-chart-legend">
              <div className="admin-chart-legend-item">
                <div className="admin-chart-legend-dot" style={{ background: PURPLE }} />
                {t('admin.stats.chart_new_regs_per_month')}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

    </Space>
  );
};

export default AdminStats;
